import {
  getEmbedStatsData,
  getHostingStatsData,
  getStorageStatsData,
  getText2SpeechData,
  getTranscribeStatsData
} from '@anm/components/user/UserLimits/helpers';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { userActions, userSelectors } from '../../../store/modules/user';

import { getIsPending } from './helpers';

export const useFetchAllStats = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(userActions.fetchEmbedStats());
    dispatch(userActions.fetchHostingStats());
    dispatch(userActions.fetchStorageStats());
    dispatch(userActions.fetchTranscribeStats());
    dispatch(userActions.fetchTextToSpeechStats());
  }, []);
};

export const useLimitsData = () => {
  const embedStats = useSelector(userSelectors.selectUserEmbedStats);
  const storageStats = useSelector(userSelectors.selectUserStorageStats);
  const hostingStats = useSelector(userSelectors.selectUserHostingStats);
  const transcribeStats = useSelector(userSelectors.selectUserTranscribeStats);
  const textToSpeechData = useSelector(userSelectors.selectUserTextToSpeechStats);
  const waveSubscription = useSelector(userSelectors.selectWaveSubscription);

  const subscriptionFeatures = waveSubscription?.features;

  const embedIsPending = getIsPending(embedStats);
  const storageIsPending = getIsPending(storageStats);
  const hostingIsPending = getIsPending(hostingStats);
  const transcribeIsPending = getIsPending(transcribeStats);
  const textToSpeechIsPending = getIsPending(textToSpeechData);

  const isStatsPending =
    embedIsPending || storageIsPending || hostingIsPending || transcribeIsPending || textToSpeechIsPending;

  const extraStorageCost = subscriptionFeatures?.Hosting.extraStoragePerGBPrice || 0;
  const extraEmbedCost = subscriptionFeatures?.Hosting.extraEmbedPrice || 0;
  const extraTrafficCost = subscriptionFeatures?.Hosting.extraTrafficPerGBPrice || 0;
  const extraTranscribeCost = subscriptionFeatures?.Transcribe.pricePerMinuteExtra || 0;
  const extraTextToSpeechCost = subscriptionFeatures?.Text2Speech.pricePerThousandExtra || 0;

  const limitsData = [
    getStorageStatsData({
      data: storageStats?.data,
      extraItemCost: extraStorageCost,
      canPurchase: subscriptionFeatures?.Hosting.canPurchaseStorage
    }),
    getEmbedStatsData({
      data: embedStats?.data,
      extraItemCost: extraEmbedCost,
      canPurchase: subscriptionFeatures?.Hosting.canPurchaseEmbeds
    }),
    getHostingStatsData({
      data: hostingStats?.data,
      extraItemCost: extraTrafficCost,
      canPurchase: subscriptionFeatures?.Hosting.canPurchaseTraffic
    }),
    getTranscribeStatsData({
      data: transcribeStats?.data,
      extraItemCost: extraTranscribeCost,
      canPurchase: subscriptionFeatures?.Transcribe.canPurchaseMinutes
    }),
    getText2SpeechData({
      data: textToSpeechData?.data,
      extraItemCost: extraTextToSpeechCost,
      canPurchase: subscriptionFeatures?.Text2Speech.canPurchaseChars
    })
  ];

  const perMonthCharge = limitsData.slice(0, 2).reduce((acc, { cost }) => acc + (cost ? cost / 100 : 0), 0);
  const thisMonthCharge = limitsData.slice(2).reduce((acc, { cost }) => acc + (cost ? cost / 100 : 0), 0);

  return {
    perMonthCharge,
    thisMonthCharge,
    data: limitsData,
    isPending: isStatsPending
  } as const;
};
